/* eslint-disable @typescript-eslint/no-use-before-define */
import { Stack, styled as styledMUI } from '@mui/material';
import SideBar from 'components/SideBar/SideBar';
import TopBar from 'components/TopBar/TopBar';
import { useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { TUserState } from 'typings';
import { ROUTES } from 'utils/constants';
import { isAndroidWebView, isRunningInWebView } from 'utils/webviewHelpers';

interface ContentContainerProps {
  customStyling: boolean;
}

const ContentContainer = styledMUI('div')<ContentContainerProps>(
  ({ theme, customStyling }) => ({
    [theme.breakpoints.down('sm')]: {
      padding: customStyling ? '0px' : '20px',
      // The Android app draws its WebView edge-to-edge without applying window
      // insets, so the 3-button nav bar (~48dp on Samsung) covers the bottom
      // of every page. Android WebView does not report system bars
      // through env(safe-area-inset-bottom) — verified 0 on an S24+ — so the
      // only thing the page can do is reserve the space blind. Costs ~24px of
      // slack on gesture-nav phones. Remove the 48px once the app applies
      // systemBars() insets to the WebView. env() is kept for iOS, where the
      // home-indicator inset is reported.
      paddingBottom: `calc(${customStyling ? '0px' : '20px'} + ${
        isAndroidWebView ? '48px' : 'env(safe-area-inset-bottom, 0px)'
      })`,
      minHeight: '100vh'
    },
    [theme.breakpoints.up('sm')]: {
      width: '320px',
      margin: customStyling ? '0px auto 0 auto' : '20px auto 0 auto'
    },
    [theme.breakpoints.up('md')]: {
      width: '440px'
    }
  })
);

const noSideBarAllowedRoutes = [ROUTES.ADD_CAMERA_INFO, ROUTES.ADD_HUB_INFO];

const noDefaultPaddingNeededRoutes = [
  ROUTES.CHECKOUT_SESSION,
  ROUTES.PAYMENT_SUCCESS,
  ROUTES.BIRD_SKIP_TRIAL,
  ROUTES.PET_SKIP_TRIAL,
  ROUTES.BABY_SKIP_TRIAL,
  ROUTES.TRAIL_CAM_SKIP_TRIAL
];

function PrivateRoute() {
  const location = useLocation();
  const isWebViewPresent = isRunningInWebView();
  console.log('location - ', location);
  // console.log(
  //   'noSideBarAllowedRoutes.includes(location.pathname) - ',
  //   noSideBarAllowedRoutes.includes(location.pathname)
  // );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { isAuthenticated } = useAppSelector<TUserState>((state) => state.user);
  const [openSideBar, setOpenSideBar] = useState(!isMobile);

  if (!isAuthenticated && !isWebViewPresent) {
    // not logged in so redirect to login page with the return url
    return <Navigate to={ROUTES.LOGIN} />;
  }
  const isCustomRoute = noDefaultPaddingNeededRoutes.includes(
    location.pathname
  );

  // authorized so return child components
  const content = (
    <Stack
      sx={{
        flex: 1,
        // Was `height: '100%'`: a fixed 100vh that page content overflowed,
        // leaving the area below the fold painted by the <body> (a different
        // product's tint) instead of this surface. `minHeight` covers the
        // viewport and still grows with the content so the page background is
        // one colour top to bottom on every route.
        minHeight: '100vh',
        backgroundColor: (theme) => `${theme.palette.background.default}`
        // Soft, brand-derived colour washes behind the flat page tint.
        // `backdrop-filter` (the glass card variants' own blur) can only
        // ever visibly soften/refract something with actual variance behind
        // it — blurring a flat single colour just produces that same flat
        // colour, which is why glass/glassClear/glassSolid read as plain
        // translucent panels rather than glass on every page up to now
        // (confirmed live: the same CSS recipe reads as genuine frosted
        // glass the moment there's something textured behind it). Kept
        // deliberately faint — this needs to stay a page background, not a
        // hero banner — and `background-attachment: fixed` anchors it to
        // the viewport rather than stretching/repeating oddly on tall,
        // scrolling pages.
      }}
    >
      {openSideBar && (
        <SideBar setOpenSideBar={setOpenSideBar} openSideBar={openSideBar} />
      )}
      <Stack
        sx={{
          width:
            openSideBar &&
            !isMobile &&
            !noSideBarAllowedRoutes.includes(location.pathname)
              ? 'calc(100% - 340px)'
              : '100%',
          marginLeft:
            openSideBar &&
            !isMobile &&
            !noSideBarAllowedRoutes.includes(location.pathname)
              ? '340px'
              : 0,
          // Fill the viewport height, then grow with the content — without the
          // old fixed `height: '100%'` that let tall pages overflow past the
          // painted background.
          flex: 1,
          flexDirection: 'column'
        }}
      >
        {!isWebViewPresent && (
          <TopBar setOpenSideBar={setOpenSideBar} openSideBar={openSideBar} />
        )}
        <ContentContainer customStyling={isCustomRoute}>
          <Outlet />
        </ContentContainer>
      </Stack>
    </Stack>
  );

  return content;
}

export default PrivateRoute;
